import { localItem } from '@/services/local-storage/local'

export type ScannedExecutionLog = {
  blockNumber: number
  transactionHash: string
  args: { txHash: string }
}

export type TxHistoryScanCache = {
  upToBlock: number
  logs: ScannedExecutionLog[]
}

/**
 * Persisted result of the ExecutionSuccess log scan, keyed by chain and Safe. Kept in
 * localStorage so a page reload only rescans the blocks mined since the previous visit: the
 * full-history scan is ~22 chunked eth_getLogs calls on Lumia Prism, and replaying it on every
 * reload both delays the history and stresses the rate-limited public RPC.
 */
const cacheItem = (chainId: string, safeAddress: string) =>
  localItem<TxHistoryScanCache>(`txHistoryScan_v1_${chainId}_${safeAddress}`)

const isValidLog = (log: ScannedExecutionLog | undefined): boolean =>
  typeof log?.blockNumber === 'number' &&
  typeof log?.transactionHash === 'string' &&
  typeof log?.args?.txHash === 'string'

export const loadTxHistoryScanCache = (chainId: string, safeAddress: string): TxHistoryScanCache | undefined => {
  const cached = cacheItem(chainId, safeAddress).get()

  if (
    !cached ||
    typeof cached.upToBlock !== 'number' ||
    !Array.isArray(cached.logs) ||
    !cached.logs.every(isValidLog)
  ) {
    return undefined
  }

  return cached
}

export const saveTxHistoryScanCache = (chainId: string, safeAddress: string, cache: TxHistoryScanCache): void => {
  cacheItem(chainId, safeAddress).set(cache)
}

/** Narrows a rich ethers Event down to the fields the history mapping and the cache need. */
export const toScannedLog = (log: ScannedExecutionLog): ScannedExecutionLog => ({
  blockNumber: log.blockNumber,
  transactionHash: log.transactionHash,
  args: { txHash: log.args.txHash },
})
